import React, { useState } from 'react';
import { Button, Input } from '@attentive-platform/stem-ui';

import BrandMark from './BrandMark';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { WelcomeScreenStyles as S, ReviewScreenStyles as RS } from './styles';

const STEPS = [
  {
    n: '1',
    title: 'Pick what you bid',
    body: 'Tick the items you actually estimate under each trade. Skip the ones you don’t.'
  },
  {
    n: '2',
    title: 'Answer a few questions',
    body: 'Small trade-level questions on how you want it measured and reported.'
  },
  {
    n: '3',
    title: 'Submit',
    body: 'Every future takeoff you send us follows this scope. No re-explaining.'
  }
];

const startButtonLabel = ({ readOnly, isReopened, showWelcomeBack, hasInactiveItems }) => {
  if (readOnly) return 'View your scope';
  if (hasInactiveItems) return 'Review the changes';
  if (isReopened) return 'Review and revise';
  if (showWelcomeBack) return 'Continue';
  return 'Start';
};

const WelcomeScreen = ({
  aeName,
  prospectCompany,
  tradeCount,
  timeEstimate = 'About 12 minutes',
  showWelcomeBack,
  welcomeBackTitle,
  welcomeBackBody,
  initialEmail = '',
  onStart,
  starting = false,
  startError = '',
  readOnly = false,
  isReopened = false,
  submittedOn = '',
  hasInactiveItems = false
}) => {
  const [email, setEmail] = useState(initialEmail);
  const canStart = readOnly ? !starting : !!(email.trim() && isValidEmail(email.trim()) && !starting);
  const buttonLabel = startButtonLabel({ readOnly, isReopened, showWelcomeBack, hasInactiveItems });

  return (
    <S.PageWrap>
      <S.Content>
        <S.BrandRow>
          <BrandMark />
          <RS.Divider />
          <span>Scope form</span>
        </S.BrandRow>

        <S.Card>
          <S.TitleBlock>
            <S.Headline>Tell us exactly what to measure on your projects</S.Headline>
            <S.Lede>
              {aeName ? `${aeName} at Beam` : 'Beam'} set this up
              {prospectCompany ? ` for ${prospectCompany}` : ''}. Fill it in once and every takeoff we do for you
              follows it — no re-explaining on each project.
            </S.Lede>
          </S.TitleBlock>

          <S.StatStrip>
            <S.StatCol>
              <S.StatLabel>Time needed</S.StatLabel>
              <S.StatValue>{timeEstimate}</S.StatValue>
            </S.StatCol>
            {aeName && (
              <S.StatCol>
                <S.StatLabel>Sent by</S.StatLabel>
                <S.StatValue>{aeName} · Beam</S.StatValue>
              </S.StatCol>
            )}
            {tradeCount != null && (
              <S.StatCol>
                <S.StatLabel>Trades</S.StatLabel>
                <S.StatValue>
                  {tradeCount} trade{tradeCount === 1 ? '' : 's'}
                </S.StatValue>
                <S.StatHint>one tab each · skip what you don&apos;t bid</S.StatHint>
              </S.StatCol>
            )}
          </S.StatStrip>

          <S.StepsGrid>
            {STEPS.map(s => (
              <S.StepCard key={s.n}>
                <S.StepBadge>{s.n}</S.StepBadge>
                <S.StepTitle>{s.title}</S.StepTitle>
                <S.StepBody>{s.body}</S.StepBody>
              </S.StepCard>
            ))}
          </S.StepsGrid>

          {!readOnly && (
            <S.EmailBlock>
              <S.InputLabel>Your email</S.InputLabel>
              <Input
                size='large'
                placeholder='you@company.com'
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </S.EmailBlock>
          )}

          {showWelcomeBack && (
            <S.WelcomeBack>
              <S.WelcomeBackDot />
              <S.WelcomeBackText>
                <S.WelcomeBackTitle>{welcomeBackTitle}</S.WelcomeBackTitle>
                <S.WelcomeBackBody>{welcomeBackBody}</S.WelcomeBackBody>
              </S.WelcomeBackText>
            </S.WelcomeBack>
          )}

          {!readOnly && (
            <S.Disclaimer>
              <S.DisclaimerDot />
              <S.DisclaimerText>
                Best done in one sitting — your answers are sent when you submit, so leaving this page loses them.
              </S.DisclaimerText>
            </S.Disclaimer>
          )}

          <S.StartRow>
            <Button
              variant='contained'
              color='primary'
              size='large'
              disabled={!canStart}
              loading={starting}
              onClick={() => onStart && onStart(readOnly ? initialEmail : email.trim())}>
              {buttonLabel}
            </Button>
            <S.StartHint>
              {startError
                ? startError
                : starting
                  ? 'Loading your scope form…'
                  : readOnly
                    ? submittedOn
                      ? `Submitted ${submittedOn} — read-only.`
                      : 'Submitted — read-only.'
                    : canStart
                      ? 'You can change trades any time'
                      : 'Enter your email to begin'}
            </S.StartHint>
          </S.StartRow>
        </S.Card>
      </S.Content>
    </S.PageWrap>
  );
};

export default WelcomeScreen;
